import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Container,
  Typography,
  Paper,
  Grid,
  Box,
  FormGroup,
  IconButton,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Modal,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  DialogContentText,
} from "@mui/material";
import {
  Add,
  AddCircleOutline,
  Delete,
  ErrorOutline,
  Warning,
} from "@mui/icons-material";
import TicketsImage from "../../Images/tickets.png";
import BandsImage from "../../Images/bands.png";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";
import {
  useAddTicketTypeMutation,
  useGetProfileInformationQuery,
  useGetTicketTypesQuery,
} from "../../Redux/apis/ApiSlice";
import { notification } from "antd";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { TimePicker } from "antd";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const ticketTypes = [
  { value: "Ticket", label: "Pass", image: TicketsImage },
  { value: "Band", label: "Band", image: BandsImage },
];

const CreateTicketsScreen = () => {
  const [missingFieldsQueue, setMissingFieldsQueue] = useState([]);

  const { data: profileData, refetch: fetchProfileData } =
    useGetProfileInformationQuery();
  const paymentIntegrated = profileData?.user?.onBoarding;
  const navigate = useNavigate();
  const [eventData, setEventData] = useState({
    eventName: "",
    description: "",
    location: "",
    organization: "",
    eventDate: "",
    date: dayjs().format("MM/DD/YYYY"),
    time: dayjs().format("hh:mm A"),
    endTime: dayjs().add(1, "hour").format("hh:mm A"),
    ticketType: "Ticket",
    endDateTime: "",
    startDateTime: "",
    ticketCategories: [
      { ticketTypeId: "", typeName: "", price: 0, quantity: "" },
    ],
  });
  const [addTicketType] = useAddTicketTypeMutation();

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [newCategory, setNewCategory] = React.useState("");
  const {
    data: ticketList,
    isLoading,
    isError,
    refetch,
  } = useGetTicketTypesQuery();
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const handleSaveCategory = async () => {
    if (!newCategory.trim()) {
      notification.error({
        message: "Type name is required!",
      });
      return;
    }

    try {
      const response = await addTicketType({
        typeName: newCategory.trim(),
      }).unwrap();
      notification.success({
        message: "Ticket type added successfully!",
      });
      handleClose(); // Close modal after successful save
      refetch();
      setNewCategory(""); // Clear input
    } catch (error) {
      console.error("Failed to add ticket type:", error);
      notification.error({
        message:
          "Failed to add ticket type!",
      });
    }
  };

  const handleChange = (e) => {
    setEventData({ ...eventData, [e.target.name]: e.target.value });
  };

  const handleAddCategory = () => {
    if (ticketList?.ticketType?.length <= eventData?.ticketCategories?.length) {
      return;
    }

    setEventData((prev) => ({
      ...prev,
      ticketCategories: [
        ...prev.ticketCategories,
        { ticketTypeId: "", typeName: "", price: 0, quantity: "" },
      ],
    }));
  };

  const handleCategoryChange = (index, field, value) => {
    const updatedCategories = [...eventData.ticketCategories];

    if (!paymentIntegrated && field === "price") {
      notification.info({
        message: "Connect Your Payment Account!",
        btn: (
          <Button
            onClick={() => {
              notification.destroy();
              sessionStorage.setItem(
                "navigationState",
                JSON.stringify({ from: "login" })
              );
              navigate("/settings");
            }}
            sx={{
              backgroundColor: "var(--secondary-color)",
              color: "white",
              mt: 1,
              "&:hover": {
                backgroundColor: "var(--primary-color)",
              },
            }}
          >
            Connect Now
          </Button>
        ),
        duration: 4,
      });

      // Reset price to 0 if payment is not integrated
      updatedCategories[index].price = 0;
    } else {
      // Update the specified field
      updatedCategories[index][field] = value;
    }

    setEventData((prev) => ({
      ...prev,
      ticketCategories: updatedCategories,
    }));
  };

  const handleRemoveCategory = (index) => {
    setEventData((prev) => ({
      ...prev,
      ticketCategories: prev.ticketCategories.filter((_, i) => i !== index),
    }));
  };

  const onStartDateTimeChange = (newValue) => {
    setEventData((prev) => ({
      ...prev,
      startDateTime: newValue ? newValue.toISOString() : null,
    }));
  };

  const onEndDateTimeChange = (newValue) => {
    setEventData((prev) => ({
      ...prev,
      endDateTime: newValue ? newValue.toISOString() : null,
    }));
  };

  const validateTimes = (startTime, endTime) => {
    const start = dayjs(startTime);
    const end = dayjs(endTime);

    if (!start.isBefore(end)) {
      console.error("Error: Start time must be earlier than end time.");
      return true;
    }

    return false;
  };

  const validateForm = () => {
    const errors = [];

    let missing = [];
    if (eventData.eventName === "") missing.push("Event Name");

    if (eventData.description === "") missing.push("Description");
    if (eventData.location === "") missing.push("Location");
    if (eventData.organization === "") missing.push("Organization Name");
    if (eventData.startDateTime === "") missing.push("Start Date & Time");
    if (eventData.endDateTime === "") missing.push("End Date & Time");
    if (
      eventData.endDateTime &&
      eventData.endDateTime &&
      validateTimes(eventData.startDateTime, eventData.endDateTime)
    )
      missing.push("Start time must be before End time");

    if (missing.length > 0) {
      setMissingFieldsQueue(missing);
      setErrorModal(true);
      return;
    }

    /*     const requiredFields = [
      "eventName",
      "description",
      "location",
      "organization",
      "startDateTime",
      "endDateTime",
    ];

    for (const field of requiredFields) {
      if (!eventData[field]) {
        setErrorMessages([`${field.replace(/([A-Z])/g, " $1")} is required.`]);
        missing.push(`${field.replace(/([A-Z])/g, " $1")} is required.`);
        setErrorModal(true);
        return false;
      }
    } */

    // ✅ Check if start time is before end time

    if (eventData.ticketCategories.length === 0) {
      missing.push("At least one ticket type is required.");
      setMissingFieldsQueue(missing);
      setErrorModal(true);
      return false;
    }

    for (let index = 0; index < eventData.ticketCategories.length; index++) {
      const category = eventData.ticketCategories[index];

      if (category.price === "" || isNaN(Number(category.price))) {
        missing.push(`Ticket type ${index + 1} must have a valid price.`);
        setMissingFieldsQueue(missing);
        setErrorModal(true);
        return false;
      }
      if (category.quantity === "" || isNaN(Number(category.quantity))) {
        missing.push(`Ticket type ${index + 1} must have a valid quantity.`);
        setMissingFieldsQueue(missing);
        setErrorModal(true);
        return false;
      }
      if (category?.quantity && category?.quantity < 1) {
        missing.push(
          `Ticket category ${index + 1} quantity must be greater than 0.`
        );
        setMissingFieldsQueue(missing);
        setErrorModal(true);
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    if (validateForm()) {
      navigate("/show-templates", { state: { eventData } });
    }
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ minHeight: "calc(100vh - 135px)", position: "relative" }}>
          <Box display="flex" justifyContent="center" mb={2}></Box>

          <Box p={{ md: 6, xs: 2 }}>
            <Typography
              variant="body2"
              align="left"
              color="text.secondary"
              gutterBottom
              sx={{ mb: 2 }}
            >
              {/* Fill in the details below to design your ticket. */}
              Fill in the details below to create your ticket for the event.
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                {[
                  { name: "eventName", label: "Event Name" },
                  {
                    name: "description",
                    label: "Description",
                    multiline: true,
                    rows: 3,
                  },
                  { name: "location", label: "Location" },
                  { name: "organization", label: "Organization" },
                ].map((field) => (
                  <Grid item xs={12} key={field.name} mb={2}>
                    <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
                      {field.label} *
                    </Typography>
                    <TextField
                      id={field.name}
                      name={field.name}
                      fullWidth
                      variant="outlined"
                      value={eventData[field.name]}
                      onChange={handleChange}
                      multiline={field.multiline}
                      rows={field.rows}
                      placeholder={`Enter ${field.label}`}
                      InputLabelProps={{ shrink: false }}
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <Box
                    mb={2}
                    sx={{ width: "100%", "& > *": { width: "100%" } }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
                      Start Date & Time *
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        size="large"
                        format="MM/DD/YYYY hh:mm A" // Adjust format as needed
                        value={
                          eventData?.startDateTime
                            ? dayjs(eventData.startDateTime)
                            : null
                        }
                        onChange={onStartDateTimeChange}
                        disablePast
                        slotProps={{
                          textField: { fullWidth: true, readOnly: true },
                        }}
                        placeholder="Select Start Date & Time"
                        shouldDisableDate={(date) =>
                          date.isBefore(dayjs().startOf("day"))
                        }
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    mb={2}
                    sx={{ width: "100%", "& > *": { width: "100%" } }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
                      End Date & Time *
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        size="large"
                        format="MM/DD/YYYY hh:mm A" // Adjust format as needed
                        value={
                          eventData?.endDateTime &&
                          dayjs(eventData.endDateTime).isValid()
                            ? dayjs(eventData.endDateTime)
                            : null
                        }
                        onChange={onEndDateTimeChange}
                        disablePast
                        slotProps={{
                          textField: { fullWidth: true, readOnly: true },
                        }}
                        placeholder="Select End Date & Time"
                        shouldDisableDate={(date) =>
                          eventData.startDateTime
                            ? date.isBefore(
                                dayjs(eventData.startDateTime).startOf("day")
                              )
                            : date.isBefore(dayjs().startOf("day"))
                        }
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Grid item xs={12} mb={2}>
                  <Typography>Ticket Categories:</Typography>
                  <FormGroup row>
                    {ticketTypes.map((type) => (
                      <div
                        key={type.value}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          textAlign: "center",
                        }}
                      >
                        <Paper
                          variant="outlined"
                          style={{
                            margin: "3px",
                            display: "flex",
                            alignItems: "center",
                            border:
                              eventData.ticketType === type.value
                                ? "2px solid #1976d2"
                                : "1px solid #ccc",
                            cursor: "pointer",
                            position: "relative",
                            borderRadius: 5,
                          }}
                          onClick={() =>
                            setEventData({
                              ...eventData,
                              ticketType: type.value,
                            })
                          }
                        >
                          <Checkbox
                            checked={eventData.ticketType === type.value}
                            value={type.value}
                            icon={
                              <Box
                                sx={{
                                  width: { xs: 100, sm: 200 }, // 100px on mobile, 200px on desktop
                                  height: { xs: 100, sm: 200 },
                                }}
                              >
                                <img
                                  src={type.image}
                                  alt={type.label}
                                  style={imageStyle}
                                />
                              </Box>
                            }
                            checkedIcon={
                              <Box
                                sx={{
                                  width: { xs: 100, sm: 200 }, // 100px on mobile, 200px on desktop
                                  height: { xs: 100, sm: 200 },
                                }}
                              >
                                <img
                                  src={type.image}
                                  alt={type.label}
                                  style={imageStyle}
                                />
                              </Box>
                            }
                            style={{ display: "none" }}
                          />
                          <FormControlLabel
                            control={
                              <Box
                                sx={{
                                  width: { xs: 100, sm: 200 }, // 100px on mobile, 200px on desktop
                                  height: { xs: 100, sm: 200 },
                                }}
                              >
                                <img
                                  src={type.image}
                                  alt={type.label}
                                  style={imageStyle}
                                />
                              </Box>
                            }
                            style={{
                              margin: 0,
                              color:
                                eventData.ticketType === type.value
                                  ? "#1976d2"
                                  : "black",
                            }}
                          />
                          {eventData.ticketType === type.value && (
                            <CheckCircleIcon style={circleIcon} />
                          )}
                        </Paper>
                        <Typography textAlign={"left"}>{type.label}</Typography>
                      </div>
                    ))}
                  </FormGroup>
                </Grid>

                <Grid item xs={12}>
                  <Typography mb={2}>Ticket Types:</Typography>

                  {eventData.ticketCategories.length > 0 ? (
                    <Box
                      sx={{
                        maxHeight: "27vh",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                    >
                      {eventData.ticketCategories.map((category, index) => (
                        <>
                          <Box mt={2}>
                            <Grid
                              container
                              spacing={2}
                              key={index}
                              alignItems="center"
                              mb={1}
                              sx={blackThemeInput}
                            >
                              {/* Ticket Type Dropdown */}

                              <Grid item xs={10} md={4}>
                                <FormControl fullWidth>
                                  <Select
                                    displayEmpty
                                    placeholder="Select a ticket type"
                                    value={category.ticketTypeId || ""}
                                    onChange={(e) => {
                                      const selectedType =
                                        ticketList?.ticketType?.find(
                                          (type) =>
                                            type.ticketTypeId === e.target.value
                                        );

                                      if (selectedType) {
                                        handleCategoryChange(
                                          index,
                                          "ticketTypeId",
                                          selectedType.ticketTypeId
                                        );
                                        handleCategoryChange(
                                          index,
                                          "typeName",
                                          selectedType.typeName
                                        ); // Store full object
                                      }
                                    }}
                                    sx={{
                                      backgroundColor: "#fff",
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "black",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "black",
                                        },
                                      "& .MuiSelect-select": { color: "black" },
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      Select Type
                                    </MenuItem>
                                    {category?.ticketTypeId && (
                                      <MenuItem
                                        key={category?.ticketTypeId}
                                        value={category.ticketTypeId}
                                      >
                                        {category.typeName}
                                      </MenuItem>
                                    )}
                                    {!isLoading &&
                                      !isError &&
                                      ticketList?.ticketType
                                        ?.filter(
                                          (t) =>
                                            !eventData.ticketCategories.some(
                                              (category) =>
                                                category.ticketTypeId ===
                                                t.ticketTypeId
                                            )
                                        )
                                        .map((type) => (
                                          <MenuItem
                                            key={type.ticketTypeId}
                                            value={type.ticketTypeId}
                                          >
                                            {type.typeName}
                                          </MenuItem>
                                        ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={10} md={3}>
                                <TextField
                                  label="Price ($)"
                                  type="number"
                                  fullWidth
                                  disabled={
                                    !paymentIntegrated ||
                                    category.ticketTypeId === ""
                                  }
                                  value={
                                    !paymentIntegrated ? 0 : category.price
                                  }
                                  onChange={(e) =>
                                    handleCategoryChange(
                                      index,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "black",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "black",
                                        },
                                    },
                                  }}
                                />
                              </Grid>

                              <Grid item xs={10} md={3}>
                                <TextField
                                  label="Quantity"
                                  type="number"
                                  fullWidth
                                  value={category.quantity}
                                  onChange={(e) =>
                                    handleCategoryChange(
                                      index,
                                      "quantity",
                                      e.target.value
                                    )
                                  }
                                  disabled={!category.ticketTypeId}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "black",
                                        },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                          borderColor: "black",
                                        },
                                    },
                                  }}
                                />
                              </Grid>

                              {/* Delete Icon */}
                              <Grid item xs={2} md={2}>
                                <IconButton
                                  color="error"
                                  onClick={() => handleRemoveCategory(index)}
                                >
                                  <Delete />
                                </IconButton>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                {/* <IconButton color="error" onClick={handleAddCategory}>
                          <AddCircleOutline color="primary" />
                        </IconButton> */}
                              </Grid>
                            </Grid>
                          </Box>

                          <Divider />
                        </>
                      ))}
                    </Box>
                  ) : (
                    <></>
                  )}

                  {!paymentIntegrated && (
                    <Box display="flex" alignItems="center">
                      <IconButton>
                        <Warning />
                      </IconButton>
                      <Typography variant="body2">
                        Price can't be entered without payment integration
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ my: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddCategory}
                      sx={{
                        flex: 1,
                        fontSize: {
                          xs: "10px",
                          sm: "12px",
                          md: "12px",
                          lg: "12px",
                          xl: "12px",
                        },
                      }}
                    >
                      Add Type
                    </Button>
                  </Box>
                  <Typography variant="body2">
                    You can add new ticket types from the settings or{" "}
                    <span style={{ fontWeight: "bold" }} onClick={handleOpen}>
                      <a href="#">directly from here</a>
                    </span>
                  </Typography>
                  {/* </Box> */}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            position: { md: "static", xs: "static" },
            bottom: 0,
            left: 0,
            width: "100%",
            background: "#fff",
            padding: "12px 12px",
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #e0e0e0",
            zIndex: 1201,
            minHeight: "64px",
          }}
        >
          <Button sx={NextBtnStyles} variant="contained" onClick={handleNext}>
            Next
          </Button>
        </Box>

        <Dialog
          open={errorModal}
          onClose={() => setErrorModal(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Incomplete Required Fields
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Please complete the following required fields before proceeding:
            </DialogContentText>

            <List>
              {missingFieldsQueue.map((field, index) => (
                <ListItem
                  key={index}
                  sx={{ alignItems: "center", paddingY: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: "30px", marginRight: "-4px" }}>
                    <ErrorOutline color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={field} sx={{ margin: 0 }} />
                </ListItem>
              ))}
            </List>
          </DialogContent>

          <DialogActions>
            <Button
              sx={{
                mt: { md: 1, xs: 5 },
                ml: 1,
                border: "1px solid #2E728F",
                color: "#061F44",
                borderRadius: "4px",
                width: { md: 120, sm: 200, xs: 200 },
                height: 35,
              }}
              onClick={() => {
                setErrorModal(false);
              }}
            >
              Okay
            </Button>
          </DialogActions>
        </Dialog>

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%", // Adjust for mobile
              maxWidth: 400, // Limit for larger screens
              bgcolor: "black",
              color: "white",
              boxShadow: 24,
              p: { xs: 3, md: 4 }, // Responsive padding
              borderRadius: "12px",
              border: "1px solid white",
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h5"
              component="h2"
              sx={{
                mb: 2,
                fontWeight: "bold",
                textAlign: "center",
                fontSize: { xs: "1.25rem", md: "1.5rem" }, // Responsive font size
              }}
            >
              Add Ticket Type
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type Name"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              InputProps={{
                style: {
                  backgroundColor: "white",
                  color: "black",
                  borderRadius: "8px",
                },
              }}
              sx={{ mb: 3 }}
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                fullWidth
                onClick={handleSaveCategory}
                sx={{
                  bgcolor: "white",
                  color: "black",
                  "&:hover": { bgcolor: "#e0e0e0" },
                  fontWeight: "bold",
                  borderRadius: "8px",
                }}
              >
                Save
              </Button>

              <Button
                variant="outlined"
                fullWidth
                onClick={handleClose}
                sx={{
                  borderColor: "white",
                  color: "white",
                  "&:hover": { bgcolor: "white", color: "black" },
                  fontWeight: "bold",
                  borderRadius: "8px",
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>
      </motion.div>
    </Box>
  );
};

export default CreateTicketsScreen;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const imageStyle = {
  height: "100%",
  width: "100%",
  objectFit: "contain",
};
const circleIcon = {
  position: "absolute",
  bottom: "-5px",
  right: "-5px",
  color: "#1976d2",
  background: "white",
  borderRadius: "50%",
};

const blackThemeInput = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "black" },
    "&:hover fieldset": { borderColor: "black" },
    "&.Mui-focused fieldset": { borderColor: "black" },
  },
  "& .MuiInputBase-input": { color: "black" },
};

const NextBtnStyles = {
  backgroundColor: "var(--secondary-color)",
  color: "white",
  mt: { md: 1, xs: 1 },
  ml: 1,
  borderRadius: "4px",
  width: 200,
  height: 35,
  "&:hover": {
    backgroundColor: "var(--primary-color)",
  },
};
